import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  ArrowLeft, ArrowRight, Upload, Trash2, FileText, CheckCircle,
  AlertCircle, Calculator, Image, X, Download, Save, MessageSquare
} from "lucide-react";
import { toast } from "sonner";
import ContactSupportModal from "../components/ContactSupportModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// California Flag component
const CaliforniaFlag = ({ className = "w-8 h-8" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_green-builder-3/artifacts/dnderd21_Cali%20FLAG.png" 
    alt="California" 
    className={`${className} object-contain`}
  />
);

const FinalReportWizard = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [project, setProject] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Form state
  const [actualMaterials, setActualMaterials] = useState([]);
  const [varianceExplanations, setVarianceExplanations] = useState({});
  const [completionDate, setCompletionDate] = useState(new Date().toISOString().split('T')[0]);
  const [preparerName, setPreparerName] = useState(user?.name || "");
  const [preparerTitle, setPreparerTitle] = useState("");
  
  // Receipt state
  const [receipts, setReceipts] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  
  // Calculations
  const [calculations, setCalculations] = useState({
    totalActual: 0,
    totalRecycled: 0,
    totalLandfill: 0,
    diversionRate: 0,
    meetsRequirement: false
  });

  const steps = [
    { id: 1, name: "Review Estimates", description: "Review original CWMP estimates" },
    { id: 2, name: "Enter Actuals", description: "Enter actual waste quantities" },
    { id: 3, name: "Upload Receipts", description: "Upload weight tickets & receipts" },
    { id: 4, name: "Variance Notes", description: "Explain any variances" },
    { id: 5, name: "Generate Report", description: "Review and generate PDF" }
  ];

  // Fetch project and status
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        
        // Fetch project
        const projectRes = await fetch(`${API}/projects/${projectId}`, {
          credentials: 'include',
          headers
        });
        
        if (!projectRes.ok) throw new Error('Project not found');
        const projectData = await projectRes.json();
        setProject(projectData);
        
        // Check admin status
        const adminRes = await fetch(`${API}/auth/check-admin`, {
          credentials: 'include',
          headers
        });
        if (adminRes.ok) {
          const adminData = await adminRes.json();
          setIsAdmin(adminData.is_admin);
        }
        
        // Fetch final report status
        const statusRes = await fetch(`${API}/projects/${projectId}/final-report-status`, {
          credentials: 'include',
          headers
        });
        
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          
          // Initialize materials from estimates or existing actuals
          if (statusData.actual_waste?.materials?.length > 0) {
            setActualMaterials(statusData.actual_waste.materials);
          } else {
            // Initialize from estimates
            const estimatedMaterials = statusData.estimated_materials || projectData.materials || [];
            setActualMaterials(estimatedMaterials.map(m => ({
              material: m.material,
              actual_tons: m.estimated_tons || 0,
              recycled_tons: m.diverted_tons || (m.estimated_tons * 0.65),
              landfill_tons: m.landfill_tons || (m.estimated_tons * 0.35),
              facility_name: "",
              weight_ticket_ref: ""
            })));
          }
          
          // Load existing variance explanations
          if (statusData.variance_data?.explanations) {
            setVarianceExplanations(statusData.variance_data.explanations);
          }
          
          // Load receipts
          setReceipts(projectData.receipts?.map(id => ({ file_id: id })) || []);
        }
        
      } catch (error) {
        console.error('Error fetching project:', error);
        toast.error('Failed to load project');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [projectId, navigate, user?.name]);

  // Calculate totals whenever materials change
  useEffect(() => {
    const totalActual = actualMaterials.reduce((sum, m) => sum + (parseFloat(m.actual_tons) || 0), 0);
    const totalRecycled = actualMaterials.reduce((sum, m) => sum + (parseFloat(m.recycled_tons) || 0), 0);
    const totalLandfill = actualMaterials.reduce((sum, m) => sum + (parseFloat(m.landfill_tons) || 0), 0);
    const diversionRate = totalActual > 0 ? (totalRecycled / totalActual) * 100 : 0;
    const targetRate = project?.target_diversion || 65;
    
    setCalculations({
      totalActual: totalActual.toFixed(2),
      totalRecycled: totalRecycled.toFixed(2),
      totalLandfill: totalLandfill.toFixed(2),
      diversionRate: diversionRate.toFixed(1),
      meetsRequirement: diversionRate >= targetRate
    });
  }, [actualMaterials, project?.target_diversion]);

  // Handle material update
  const handleMaterialChange = (index, field, value) => {
    const updated = [...actualMaterials];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    
    // Auto-calculate landfill if recycled changes
    if (field === 'recycled_tons') {
      const actual = parseFloat(updated[index].actual_tons) || 0;
      const recycled = parseFloat(value) || 0;
      updated[index].landfill_tons = Math.max(0, actual - recycled).toFixed(2);
    }
    
    // Auto-calculate recycled if actual changes and recycled is proportionally set
    if (field === 'actual_tons') {
      const actual = parseFloat(value) || 0;
      const oldActual = parseFloat(actualMaterials[index].actual_tons) || 0;
      if (oldActual > 0) {
        const ratio = parseFloat(actualMaterials[index].recycled_tons) / oldActual;
        updated[index].recycled_tons = (actual * ratio).toFixed(2);
        updated[index].landfill_tons = (actual * (1 - ratio)).toFixed(2);
      }
    }
    
    setActualMaterials(updated);
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingFile(true);
    const token = localStorage.getItem('token');
    
    for (const file of files) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error(`Invalid file type: ${file.name}. Please upload JPG, PNG, or PDF files.`);
        continue;
      }
      
      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`File too large: ${file.name}. Maximum size is 10MB.`);
        continue;
      }
      
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await fetch(`${API}/upload/${projectId}`, {
          method: 'POST',
          credentials: 'include',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
          body: formData
        });
        
        if (response.ok) {
          const data = await response.json();
          setReceipts(prev => [...prev, { 
            file_id: data.file_id, 
            filename: data.filename,
            uploaded_at: new Date().toISOString()
          }]);
          toast.success(`Uploaded: ${file.name}`);
        } else {
          throw new Error('Upload failed');
        }
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
      }
    }
    
    setUploadingFile(false);
    e.target.value = ''; // Reset input
  };

  // Handle receipt deletion
  const handleDeleteReceipt = async (fileId) => {
    if (!window.confirm('Delete this receipt?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/projects/${projectId}/receipts/${fileId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        setReceipts(receipts.filter(r => r.file_id !== fileId));
        toast.success('Receipt deleted');
      }
    } catch (error) {
      toast.error('Failed to delete receipt');
    }
  };

  // Save progress
  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/projects/${projectId}/final-report-data`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({
          actual_materials: actualMaterials.map(m => ({
            material: m.material,
            actual_tons: parseFloat(m.actual_tons) || 0,
            recycled_tons: parseFloat(m.recycled_tons) || 0,
            landfill_tons: parseFloat(m.landfill_tons) || 0,
            facility_name: m.facility_name || "",
            weight_ticket_ref: m.weight_ticket_ref || ""
          })),
          variance_explanations: varianceExplanations,
          completion_date: completionDate,
          preparer_name: preparerName,
          preparer_title: preparerTitle
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Progress saved successfully');
        return data;
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      toast.error('Failed to save progress');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Generate Final Report PDF
  const handleGenerateReport = async () => {
    // Validate variance explanation if significant variance exists
    const estimatedTotal = project.estimated_waste?.total_tons || 0;
    const actualTotal = parseFloat(calculations.totalActual) || 0;
    const variancePercent = estimatedTotal > 0 
      ? Math.abs((actualTotal - estimatedTotal) / estimatedTotal) * 100 
      : 0;
    
    if (variancePercent > 10 && (!varianceExplanations.general || varianceExplanations.general.trim().length < 20)) {
      toast.error("Please provide a detailed variance explanation (at least 20 characters). Your actual waste differs by more than 10% from estimates.");
      setCurrentStep(4); // Go back to variance step
      return;
    }
    
    // Save first
    const saveResult = await handleSave();
    if (!saveResult) return;
    
    setGenerating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/generate/final-report/${projectId}`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Generation failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FinalReport_${project.project_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Final Report generated successfully!');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setGenerating(false);
    }
  };

  // Navigation
  const nextStep = async () => {
    if (currentStep < steps.length) {
      // Auto-save on step change
      if (currentStep === 2 || currentStep === 4) {
        await handleSave();
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Project not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-primary/20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <CaliforniaFlag className="w-10 h-10" />
              <span className="font-bold text-xl text-foreground font-['Manrope']">Plan Generator</span>
              <span className="text-xl text-primary font-matrix ml-1">CA</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/dashboard')}
                className="border-primary/30"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']" data-testid="final-report-title">
            Final Verification Report
          </h1>
          <p className="text-muted-foreground mt-1">
            {project.project_name} • {project.address}, {project.city}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center ${index < steps.length - 1 ? 'flex-1' : ''}`}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep >= step.id
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground'
                  }`}
                  data-testid={`step-${step.id}`}
                >
                  {currentStep > step.id ? <CheckCircle className="w-4 h-4" /> : step.id}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${
                      currentStep > step.id ? 'bg-primary' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="text-center">
            <p className="font-medium text-foreground">{steps[currentStep - 1].name}</p>
            <p className="text-sm text-muted-foreground">{steps[currentStep - 1].description}</p>
          </div>
        </div>

        {/* Step Content */}
        <Card className="border-primary/20 mb-6">
          <CardContent className="p-6">
            {/* Step 1: Review Estimates */}
            {currentStep === 1 && (
              <div data-testid="step-1-content">
                <h3 className="text-lg font-semibold text-foreground mb-4">Original CWMP Estimates</h3>
                <p className="text-muted-foreground mb-6">
                  Review the estimated waste quantities from your Construction Waste Management Plan.
                  You will compare these to actual quantities in the next step.
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 px-2 text-muted-foreground">Material</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Est. Total (tons)</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Est. Diverted</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Est. Landfill</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(project.materials || []).map((m, idx) => (
                        <tr key={idx} className="border-b border-primary/10">
                          <td className="py-3 px-2 text-foreground capitalize">
                            {m.material?.replace('_', ' ')}
                          </td>
                          <td className="py-3 px-2 text-right text-foreground">
                            {(m.estimated_tons || 0).toFixed(2)}
                          </td>
                          <td className="py-3 px-2 text-right text-green-400">
                            {(m.diverted_tons || m.estimated_tons * 0.65).toFixed(2)}
                          </td>
                          <td className="py-3 px-2 text-right text-yellow-400">
                            {(m.landfill_tons || m.estimated_tons * 0.35).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-primary/5">
                        <td className="py-3 px-2 font-semibold text-foreground">TOTAL</td>
                        <td className="py-3 px-2 text-right font-semibold text-foreground">
                          {project.estimated_waste?.total_tons?.toFixed(2) || '0.00'}
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-green-400">
                          {((project.estimated_waste?.total_tons || 0) * 0.65).toFixed(2)}
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-yellow-400">
                          {((project.estimated_waste?.total_tons || 0) * 0.35).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Calculator className="w-5 h-5 text-primary" />
                    <span className="font-medium text-foreground">Target Diversion Rate</span>
                  </div>
                  <p className="text-2xl font-bold text-primary">{project.target_diversion || 65}%</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    CALGreen requires a minimum {project.target_diversion || 65}% diversion rate
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Enter Actual Quantities */}
            {currentStep === 2 && (
              <div data-testid="step-2-content">
                <h3 className="text-lg font-semibold text-foreground mb-4">Enter Actual Waste Quantities</h3>
                
                {/* Helpful explanation box */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    How to Complete This Step
                  </h4>
                  <ul className="text-sm text-blue-800 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-blue-600">1.</span>
                      <span><strong>Gather your weight tickets</strong> - These are receipts from recycling facilities and landfills showing the weight of materials delivered.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-blue-600">2.</span>
                      <span><strong>Actual Total</strong> - Enter the total weight from your weight ticket for each material type.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-green-600">3.</span>
                      <span><strong>Recycled (Green)</strong> - Materials sent to recycling facilities, MRFs, or reuse centers. This counts toward your 65% diversion goal!</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-yellow-600">4.</span>
                      <span><strong>Landfill (Yellow)</strong> - Materials disposed at a landfill. Aim to minimize this.</span>
                    </li>
                  </ul>
                  <p className="mt-3 text-xs text-blue-700 bg-blue-100 p-2 rounded">
                    <strong>Tip:</strong> Recycled + Landfill should equal the Actual Total for each material.
                  </p>
                </div>
                
                <div className="space-y-4">
                  {actualMaterials.map((material, idx) => (
                    <div key={idx} className="p-4 border border-primary/20 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-foreground capitalize">
                          {material.material?.replace('_', ' ')}
                        </h4>
                        <Badge variant="outline" className="text-xs">
                          Est: {(project.materials?.[idx]?.estimated_tons || 0).toFixed(2)} tons
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <Label className="text-muted-foreground">Actual Total (tons)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={material.actual_tons}
                            onChange={(e) => handleMaterialChange(idx, 'actual_tons', e.target.value)}
                            className="mt-1 border-2 border-primary/50 bg-background"
                            data-testid={`actual-${material.material}`}
                          />
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Recycled (tons)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={material.recycled_tons}
                            onChange={(e) => handleMaterialChange(idx, 'recycled_tons', e.target.value)}
                            className="mt-1 border-2 border-green-500/50 bg-background focus:border-green-500"
                            data-testid={`recycled-${material.material}`}
                          />
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Landfill (tons)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={material.landfill_tons}
                            onChange={(e) => handleMaterialChange(idx, 'landfill_tons', e.target.value)}
                            className="mt-1 border-2 border-yellow-500/50 bg-background"
                            data-testid={`landfill-${material.material}`}
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                        <div>
                          <Label className="text-muted-foreground">Facility Name</Label>
                          <Input
                            type="text"
                            value={material.facility_name || ''}
                            onChange={(e) => handleMaterialChange(idx, 'facility_name', e.target.value)}
                            placeholder="e.g., Zanker Recycling"
                            className="mt-1 border-2 border-primary/50 bg-background"
                          />
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Weight Ticket Ref #</Label>
                          <Input
                            type="text"
                            value={material.weight_ticket_ref || ''}
                            onChange={(e) => handleMaterialChange(idx, 'weight_ticket_ref', e.target.value)}
                            placeholder="e.g., WT-2025-1234"
                            className="mt-1 border-2 border-primary/50 bg-background"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Live Calculation Summary */}
                <div className="mt-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-medium text-foreground mb-3">Live Calculation</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Actual</p>
                      <p className="text-xl font-bold text-foreground">{calculations.totalActual} tons</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Recycled</p>
                      <p className="text-xl font-bold text-green-400">{calculations.totalRecycled} tons</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Landfill</p>
                      <p className="text-xl font-bold text-yellow-400">{calculations.totalLandfill} tons</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Diversion Rate</p>
                      <p className={`text-xl font-bold ${calculations.meetsRequirement ? 'text-green-400' : 'text-red-400'}`}>
                        {calculations.diversionRate}%
                      </p>
                    </div>
                  </div>
                  
                  {calculations.meetsRequirement ? (
                    <div className="mt-4 flex items-center gap-2 text-green-400">
                      <CheckCircle className="w-5 h-5" />
                      <span>Meets {project.target_diversion || 65}% diversion requirement</span>
                    </div>
                  ) : (
                    <div className="mt-4 flex items-center gap-2 text-red-400">
                      <AlertCircle className="w-5 h-5" />
                      <span>Below {project.target_diversion || 65}% diversion requirement</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Upload Receipts */}
            {currentStep === 3 && (
              <div data-testid="step-3-content">
                <h3 className="text-lg font-semibold text-foreground mb-4">Upload Receipts & Weight Tickets</h3>
                
                {/* Helpful explanation box */}
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    What Documents Do I Need to Upload?
                  </h4>
                  <p className="text-sm text-amber-800 mb-3">
                    Upload photos or scans of your <strong>weight tickets</strong> and <strong>facility receipts</strong>. 
                    These prove your waste was properly recycled or disposed.
                  </p>
                  
                  <div className="bg-white/50 p-3 rounded border border-amber-200">
                    <p className="text-sm font-semibold text-amber-900 mb-2">Each receipt should show:</p>
                    <ul className="text-sm text-amber-800 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span><strong>Facility name</strong> - Recycling center or landfill name</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span><strong>Date</strong> - When the material was delivered</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span><strong>Material type</strong> - Concrete, wood, metal, etc.</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span><strong>Weight</strong> - In tons or pounds</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span><strong>Destination</strong> - Recycled or Landfill</span>
                      </li>
                    </ul>
                  </div>
                  
                  <p className="mt-3 text-xs text-amber-700 bg-amber-100 p-2 rounded">
                    <strong>Tip:</strong> Take clear photos with your phone - make sure all text is readable!
                  </p>
                </div>
                
                {/* Upload Area */}
                <div className="mb-6">
                  <label 
                    className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-primary/30 rounded-lg cursor-pointer bg-primary/5 hover:bg-primary/10 transition-colors"
                    data-testid="upload-area"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploadingFile ? (
                        <>
                          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mb-3"></div>
                          <p className="text-sm text-muted-foreground">Uploading...</p>
                        </>
                      ) : (
                        <>
                          <Upload className="w-10 h-10 text-primary mb-3" />
                          <p className="mb-2 text-sm text-foreground">
                            <span className="font-semibold">Click to upload</span> or drag and drop
                          </p>
                          <p className="text-xs text-muted-foreground">
                            JPG, PNG, or PDF (max 10MB per file)
                          </p>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept=".jpg,.jpeg,.png,.pdf"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploadingFile}
                      data-testid="file-input"
                    />
                  </label>
                </div>
                
                {/* Uploaded Files List */}
                <div>
                  <h4 className="font-medium text-foreground mb-3">
                    Uploaded Documents ({receipts.length})
                  </h4>
                  
                  {receipts.length === 0 ? (
                    <div className="text-center py-8 border border-dashed border-primary/20 rounded-lg">
                      <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                      <p className="text-muted-foreground">No receipts uploaded yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {receipts.map((receipt, idx) => (
                        <div
                          key={receipt.file_id || idx}
                          className="flex items-center justify-between p-3 border border-primary/20 rounded-lg"
                          data-testid={`receipt-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                              {receipt.filename?.toLowerCase().endsWith('.pdf') ? (
                                <FileText className="w-5 h-5 text-primary" />
                              ) : (
                                <Image className="w-5 h-5 text-primary" />
                              )}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                {receipt.filename || `Receipt ${idx + 1}`}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {receipt.uploaded_at ? new Date(receipt.uploaded_at).toLocaleDateString() : 'Uploaded'}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteReceipt(receipt.file_id)}
                            className="text-destructive hover:bg-destructive/10"
                            data-testid={`delete-receipt-${idx}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="mt-6 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">Documentation Tip</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        For best results, ensure weight tickets clearly show the facility name, 
                        material type, weight in tons, and date. These will be referenced in your 
                        Final Report.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Variance Notes */}
            {currentStep === 4 && (
              <div data-testid="step-4-content">
                <h3 className="text-lg font-semibold text-foreground mb-4">Variance Explanations</h3>
                
                {/* Important Warning Callout */}
                <div className="mb-6 p-4 bg-amber-500/15 rounded-lg border-2 border-amber-500/50">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-amber-500 text-base">Important: Council Review Required</p>
                      <p className="text-sm text-foreground mt-1">
                        Councils often scrutinize variance explanations carefully. <strong>Empty or vague explanations may result in your report being rejected or delayed.</strong> Please provide clear, specific reasons for any differences between estimated and actual waste quantities.
                      </p>
                    </div>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6">
                  If your actual waste quantities differ significantly from estimates, provide 
                  explanations. This is especially important if your diversion rate is below the target.
                </p>
                
                {/* Variance Summary */}
                <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <h4 className="font-medium text-foreground mb-3">Variance Summary</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Estimated Total</p>
                      <p className="text-lg font-semibold text-foreground">
                        {project.estimated_waste?.total_tons?.toFixed(2) || '0.00'} tons
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Actual Total</p>
                      <p className="text-lg font-semibold text-foreground">
                        {calculations.totalActual} tons
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Variance</p>
                      <p className={`text-lg font-semibold ${
                        parseFloat(calculations.totalActual) > (project.estimated_waste?.total_tons || 0)
                          ? 'text-yellow-400'
                          : 'text-green-400'
                      }`}>
                        {(parseFloat(calculations.totalActual) - (project.estimated_waste?.total_tons || 0)).toFixed(2)} tons
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Achieved Diversion</p>
                      <p className={`text-lg font-semibold ${calculations.meetsRequirement ? 'text-green-400' : 'text-red-400'}`}>
                        {calculations.diversionRate}%
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* General Variance Explanation */}
                <div className="mb-6">
                  <Label className="text-foreground text-base font-medium">
                    General Variance Explanation <span className="text-amber-500">*</span>
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1 mb-2">
                    Tip: Include project changes, unforeseen conditions, or material substitutions that affected waste quantities.
                  </p>
                  <Textarea
                    value={varianceExplanations.general || ''}
                    onChange={(e) => setVarianceExplanations({...varianceExplanations, general: e.target.value})}
                    placeholder="Example: Actual waste was higher than estimated due to discovery of additional demolition requirements during construction. Concrete waste increased by 2 tons due to foundation modifications required by the structural engineer..."
                    className="mt-2 min-h-[120px] border-amber-500/30 focus:border-amber-500"
                    data-testid="variance-general"
                  />
                </div>
                
                {/* Diversion Rate Explanation (if below target) */}
                {!calculations.meetsRequirement && (
                  <div className="mb-6 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex items-start gap-3 mb-3">
                      <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">Diversion Rate Below Target</p>
                        <p className="text-sm text-muted-foreground">
                          Your achieved rate of {calculations.diversionRate}% is below the 
                          {project.target_diversion || 65}% requirement. Please explain.
                        </p>
                      </div>
                    </div>
                    <Textarea
                      value={varianceExplanations.diversion_shortfall || ''}
                      onChange={(e) => setVarianceExplanations({...varianceExplanations, diversion_shortfall: e.target.value})}
                      placeholder="Explain why the diversion rate is below the target..."
                      className="min-h-[100px]"
                      data-testid="variance-diversion"
                    />
                  </div>
                )}
                
                {/* Preparer Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <div>
                    <Label className="text-foreground">Preparer Name</Label>
                    <Input
                      value={preparerName}
                      onChange={(e) => setPreparerName(e.target.value)}
                      placeholder="John Smith"
                      className="mt-1"
                      data-testid="preparer-name"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Title</Label>
                    <Input
                      value={preparerTitle}
                      onChange={(e) => setPreparerTitle(e.target.value)}
                      placeholder="Project Manager"
                      className="mt-1"
                      data-testid="preparer-title"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Completion Date</Label>
                    <Input
                      type="date"
                      value={completionDate}
                      onChange={(e) => setCompletionDate(e.target.value)}
                      className="mt-1"
                      data-testid="completion-date"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Generate Report */}
            {currentStep === 5 && (
              <div data-testid="step-5-content">
                <h3 className="text-lg font-semibold text-foreground mb-4">Review & Generate Report</h3>
                <p className="text-muted-foreground mb-6">
                  Review your final report data before generating the PDF.
                </p>
                
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  {/* Compliance Status */}
                  <Card className={`border-2 ${calculations.meetsRequirement ? 'border-green-500/30 bg-green-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        {calculations.meetsRequirement ? (
                          <CheckCircle className="w-8 h-8 text-green-500" />
                        ) : (
                          <AlertCircle className="w-8 h-8 text-red-500" />
                        )}
                        <div>
                          <p className="font-semibold text-foreground">
                            {calculations.meetsRequirement ? 'COMPLIANT' : 'NON-COMPLIANT'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {calculations.diversionRate}% diversion achieved
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Documentation Status */}
                  <Card className="border-primary/20">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-primary" />
                        <div>
                          <p className="font-semibold text-foreground">{receipts.length} Documents</p>
                          <p className="text-sm text-muted-foreground">
                            Supporting receipts uploaded
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Final Summary Table */}
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-primary/20">
                        <th className="text-left py-3 px-2 text-muted-foreground">Material</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Actual (tons)</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Recycled</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Landfill</th>
                        <th className="text-right py-3 px-2 text-muted-foreground">Div. %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {actualMaterials.map((m, idx) => {
                        const divRate = parseFloat(m.actual_tons) > 0 
                          ? ((parseFloat(m.recycled_tons) / parseFloat(m.actual_tons)) * 100).toFixed(0)
                          : '0';
                        return (
                          <tr key={idx} className="border-b border-primary/10">
                            <td className="py-3 px-2 text-foreground capitalize">
                              {m.material?.replace('_', ' ')}
                            </td>
                            <td className="py-3 px-2 text-right text-foreground">
                              {parseFloat(m.actual_tons).toFixed(2)}
                            </td>
                            <td className="py-3 px-2 text-right text-green-400">
                              {parseFloat(m.recycled_tons).toFixed(2)}
                            </td>
                            <td className="py-3 px-2 text-right text-yellow-400">
                              {parseFloat(m.landfill_tons).toFixed(2)}
                            </td>
                            <td className="py-3 px-2 text-right text-foreground">{divRate}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-primary/5">
                        <td className="py-3 px-2 font-semibold text-foreground">TOTAL</td>
                        <td className="py-3 px-2 text-right font-semibold text-foreground">
                          {calculations.totalActual}
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-green-400">
                          {calculations.totalRecycled}
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-yellow-400">
                          {calculations.totalLandfill}
                        </td>
                        <td className="py-3 px-2 text-right font-semibold text-primary">
                          {calculations.diversionRate}%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                
                {/* Preparer Info Summary */}
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20 mb-6">
                  <h4 className="font-medium text-foreground mb-2">Report Details</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <p className="text-muted-foreground">Preparer:</p>
                    <p className="text-foreground">{preparerName || 'Not specified'}</p>
                    <p className="text-muted-foreground">Title:</p>
                    <p className="text-foreground">{preparerTitle || 'Not specified'}</p>
                    <p className="text-muted-foreground">Completion Date:</p>
                    <p className="text-foreground">{completionDate}</p>
                  </div>
                </div>
                
                {/* Generate Button */}
                <div className="text-center">
                  <Button
                    size="lg"
                    onClick={handleGenerateReport}
                    disabled={generating}
                    className="bg-white text-black hover:bg-gray-100 px-8 font-semibold"
                    data-testid="generate-report-btn"
                  >
                    {generating ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Generate Final Report PDF
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-muted-foreground mt-3">
                    This will save your data and generate a professional PDF report
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={prevStep}
            disabled={currentStep === 1}
            className="border-primary/30"
            data-testid="prev-step-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          
          <div className="flex items-center gap-3">
            {currentStep > 1 && currentStep < 5 && (
              <Button
                variant="outline"
                onClick={handleSave}
                disabled={saving}
                className="border-primary/30"
                data-testid="save-progress-btn"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Progress
                  </>
                )}
              </Button>
            )}
            
            {currentStep < 5 && (
              <Button
                onClick={nextStep}
                className="bg-white text-black hover:bg-gray-100 font-semibold"
                data-testid="next-step-btn"
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </main>

      {/* Floating Contact Support Button */}
      <div className="fixed bottom-20 right-6 z-40">
        <Button
          onClick={() => setShowContactModal(true)}
          className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg rounded-full px-4 py-6"
          data-testid="contact-support-btn"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Support
        </Button>
      </div>

      {/* Contact Support Modal */}
      <ContactSupportModal 
        isOpen={showContactModal} 
        onClose={() => setShowContactModal(false)} 
      />
    </div>
  );
};

export default FinalReportWizard;
